// Canonical Expectation Level scale.
//
// Source: docs/specs/clients-accounts-account-management.md and docs/system-design.md
// (Account entity). The value is stored as integer 1–5 on client_account.expectation_level
// and drives the Fit Score ceiling (floor cap / ceiling penalty) — see src/api/fit-score.ts.
//
// The labels, client-profile text, and "pod guidance" live here as the single source of
// truth (no DB table — the five values are fixed and versioned in code). The `podGuidance`
// string is ADVISORY DISPLAY TEXT ONLY: it never auto-sets Pod Size (Pod Size is
// Solution-owned) and never enters Fit Score math (non-negotiables #2, #11).

export interface ExpectationLevelDef {
  /** Stored value, 1–5. */
  level: number;
  /** Short name shown in selectors, e.g. "Standard". */
  label: string;
  /** Client profile description shown as helper text / tooltip. */
  clientProfile: string;
  /** Advisory staffing hint shown to the RM. Never drives Pod Size or Fit Score. */
  podGuidance: string;
}

export const EXPECTATION_LEVELS = [
  {
    level: 1,
    label: "Self-Sufficient",
    clientProfile: "Software-literate, collaborative, realistic on scope/budget. Runs themselves.",
    podGuidance: "Light-touch; any grade safe",
  },
  {
    level: 2,
    label: "Cooperative",
    clientProfile: "Easy to work with, occasional steering, reasonable expectations.",
    podGuidance: "Standard; minimal high-grade cover",
  },
  {
    level: 3,
    label: "Standard",
    clientProfile: "Normal demands; needs typical consulting support and some education.",
    podGuidance: "Solid mid-level",
  },
  {
    level: 4,
    label: "Demanding",
    clientProfile:
      "High expectations, limited software literacy or heavy hand-holding, budget-sensitive. Needs steering.",
    podGuidance: "Grade-A-led; strong consulting",
  },
  {
    level: 5,
    label: "Critical",
    clientProfile:
      "Very demanding and low literacy and budget pressure / high stakes. Every interaction matters.",
    podGuidance: "Most-equipped, highest-grade pod",
  },
] as const satisfies readonly ExpectationLevelDef[];

export const MIN_EXPECTATION_LEVEL = 1;
export const MAX_EXPECTATION_LEVEL = 5;

export function isExpectationLevel(value: number): boolean {
  return (
    Number.isInteger(value) && value >= MIN_EXPECTATION_LEVEL && value <= MAX_EXPECTATION_LEVEL
  );
}

export function expectationLevelDef(level: number): ExpectationLevelDef | undefined {
  return EXPECTATION_LEVELS.find((l) => l.level === level);
}
